import { useTranslation } from 'react-i18next';

import {
  activeFilterCount,
  type CatalogFilters,
  type CatalogSort,
  type LabelChoice,
  selectedLabelValues,
  type Offered,
  type OwnerFilter,
  type SpaceFilter,
  type TypeFilter,
} from '@/entities/catalog';
import { Button, Combobox, FilterPopover, Icons, MultiFilter } from '@/shared/ui';

import styles from './CatalogToolbar.module.css';

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

interface Props {
  query: string;
  onQuery: (query: string) => void;
  searchLabel: string;
  filters: CatalogFilters;
  onChange: (patch: Partial<CatalogFilters>) => void;
  onReset: () => void;
  /** Which filters this page offers (a workspace section already is a type). */
  offered: Offered;
  /** The orders this page offers, and the one in force. */
  sorts: readonly CatalogSort[];
  sort: CatalogSort;
  /** How many resources each type holds under the OTHER filters and the search (shown as badges). */
  counts?: Partial<Record<TypeFilter, number>>;
  /** The labels the label filter offers, with their colors (none: the control is not shown). */
  labelChoices?: readonly LabelChoice[];
  /** The types the page can show (default: all). One type or none: the type control is not shown. */
  typeChoices?: readonly TypeFilter[];
  /**
   * Which part to draw: the whole toolbar (default), only the search box, or only the filter
   * controls - the home page shows them as two separate elements.
   */
  part?: 'all' | 'search' | 'filters';
}

function Choice<T extends string>({
  legend,
  name,
  value,
  options,
  onPick,
}: {
  legend: string;
  name: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onPick: (value: T) => void;
}) {
  return (
    <fieldset className={styles.group}>
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.choices}>
        {options.map((option) => (
          <label key={option.value} className={styles.choice}>
            <input
              type="radio"
              name={name}
              checked={value === option.value}
              onChange={() => {
                onPick(option.value);
              }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * ONE toolbar for every resource catalog (home dashboard, workspace): the search, the "Filters"
 * popover (type, favorites, owner, workspace kind - what the page offers), the sort, and - below -
 * one removable chip per active popover filter (the quick toggles show their own state) with a compact reset. Filtering and sorting are separate
 * controls; changing one never touches another. On a phone the filters collapse into the popover.
 */
export function CatalogToolbar({
  query,
  onQuery,
  searchLabel,
  filters,
  onChange,
  onReset,
  offered,
  sorts,
  sort,
  counts,
  labelChoices = [],
  typeChoices = ['all', 'board', 'database', 'document'],
  part = 'all',
}: Props) {
  const { t } = useTranslation();
  // The type has a control of its own on the page (below), so the popover does not repeat it.
  // a stored selection keeps the control (and its chips) so that a vanished label can be removed
  const labelOffered =
    offered.label === true && (labelChoices.length > 0 || filters.labels.length > 0);
  const inPopover = activeFilterCount(filters, {
    ...offered,
    label: false, // the label has its own control beside the popover (a popover cannot nest one)
    type: false,
    favorites: false,
    recent: false,
    space: false,
  });
  // a stored selection as label IDs (an older filter stored names); a deleted label stays visible
  const selectedLabels = selectedLabelValues(filters.labels, labelChoices);
  const goneName = (value: string) => (UUID_LIKE.test(value) ? t('catalog.labelDeleted') : value);
  const sortNames: Record<CatalogSort, string> = {
    manual: t('catalog.sortManual'),
    opened: t('catalog.sortOpened'),
    updated: t('catalog.sortUpdated'),
    name: t('catalog.sortName'),
  };
  const typeNames: Record<TypeFilter, string> = {
    all: t('catalog.typeAll'),
    board: t('catalog.typeBoard'),
    database: t('catalog.typeDatabase'),
    document: t('catalog.typeDocument'),
  };
  const ownerNames: Record<OwnerFilter, string> = {
    any: t('catalog.ownerAny'),
    mine: t('catalog.ownerMine'),
    others: t('catalog.ownerOthers'),
  };
  const spaceNames: Record<SpaceFilter, string> = {
    any: t('catalog.spaceAny'),
    personal: t('catalog.spacePersonal'),
    team: t('catalog.spaceTeam'),
  };
  const chips: {
    key: string;
    name: string;
    color?: string | null;
    clear: Partial<CatalogFilters>;
  }[] = [];
  if (offered.owner && filters.owner !== 'any') {
    chips.push({
      key: 'owner',
      name: `${t('catalog.owner')}: ${ownerNames[filters.owner]}`,
      clear: { owner: 'any' },
    });
  }

  if (labelOffered) {
    for (const value of selectedLabels) {
      const choice = labelChoices.find((c) => c.id === value);
      chips.push({
        key: `label:${value}`,
        name: choice?.display ?? goneName(value),
        color: choice?.color,
        clear: { labels: selectedLabels.filter((v) => v !== value) },
      });
    }
  }

  const search = (
    <div className={styles.searchWrap} role="search">
      <Icons.search />
      <input
        type="search"
        className={styles.search}
        value={query}
        maxLength={100}
        placeholder={searchLabel}
        aria-label={searchLabel}
        onChange={(event) => {
          onQuery(event.target.value);
        }}
      />
    </div>
  );
  if (part === 'search') {
    return (
      <div className={styles.root} data-testid="catalog-search" data-part="search">
        {search}
      </div>
    );
  }
  const showTypes = offered.type && typeChoices.length > 2;
  const showSort = sorts.length > 0;
  return (
    <div
      className={styles.root}
      data-testid={part === 'filters' ? 'catalog-filters' : 'catalog-toolbar'}
      data-part={part === 'filters' ? 'filters' : undefined}
      {...(part === 'filters' && { role: 'group', 'aria-label': t('catalog.filtersTitle') })}
    >
      <div
        className={styles.bar}
        {...(part === 'all' && { role: 'group', 'aria-label': t('catalog.toolbar') })}
      >
        {part !== 'filters' && search}
        <FilterPopover activeCount={inPopover} onReset={onReset}>
          {offered.owner && (
            <Choice
              legend={t('catalog.owner')}
              name="catalog-owner"
              value={filters.owner}
              options={(['any', 'mine', 'others'] as const).map((value) => ({
                value,
                label: ownerNames[value],
              }))}
              onPick={(owner) => {
                onChange({ owner });
              }}
            />
          )}
        </FilterPopover>
        {labelOffered && (
          <MultiFilter
            label={t('catalog.label')}
            testId="catalog-label-filter"
            options={labelChoices.map((choice) => ({
              value: choice.id,
              label: choice.display,
              color: choice.color,
            }))}
            selected={selectedLabels}
            unavailableName={goneName}
            searchFrom={5}
            searchPlaceholder={t('catalog.labelSearch')}
            emptyText={t('catalog.labelNone')}
            unavailableLabel={t('catalog.labelGone')}
            onChange={(labels) => {
              onChange({ labels });
            }}
          />
        )}
        {showSort && (
          <Combobox
            className={styles.sort}
            label={t('catalog.sort')}
            value={sort}
            size="sm"
            options={sorts.map((value) => ({ value, label: sortNames[value] }))}
            onChange={(next) => {
              const chosen = sorts.find((value) => value === next);
              if (chosen) onChange({ sort: chosen });
            }}
          />
        )}
      </div>
      {showTypes && (
        <div
          className={styles.segmented}
          role="radiogroup"
          aria-label={t('catalog.type')}
          data-testid="catalog-type"
        >
          {typeChoices.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={filters.type === value}
              className={styles.segment}
              data-type={value}
              onClick={() => {
                onChange({ type: value });
              }}
            >
              <span>{typeNames[value]}</span>
              {counts?.[value] !== undefined && (
                <span
                  className={styles.segmentCount}
                  aria-label={t('catalog.typeCount', { count: counts[value] })}
                >
                  {counts[value]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className={`${styles.quick} scroll-strip`} role="group" aria-label={t('catalog.quick')}>
        {offered.favorites && (
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={filters.favorites}
            onClick={() => {
              onChange({ favorites: !filters.favorites });
            }}
          >
            <span aria-hidden>★</span> {t('catalog.favorites')}
          </button>
        )}
        {offered.recent && (
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={filters.recent}
            onClick={() => {
              onChange({ recent: !filters.recent });
            }}
          >
            <Icons.history /> {t('catalog.recent')}
          </button>
        )}
        {offered.space &&
          (['personal', 'team'] as const).map((space) => (
            <button
              key={space}
              type="button"
              className={styles.toggle}
              aria-pressed={filters.space === space}
              onClick={() => {
                onChange({ space: filters.space === space ? 'any' : space });
              }}
            >
              {spaceNames[space]}
            </button>
          ))}
      </div>
      {activeFilterCount(filters, { ...offered, label: labelOffered }) > 0 && (
        <div className={styles.chipRow}>
          {chips.length > 0 && (
            <ul className={styles.chips} aria-label={t('catalog.chips')}>
              {chips.map((chip) => (
                <li key={chip.key}>
                  <button
                    type="button"
                    className={styles.chip}
                    aria-label={t('catalog.removeChip', { name: chip.name })}
                    onClick={() => {
                      onChange(chip.clear);
                    }}
                  >
                    {chip.color && (
                      <span
                        aria-hidden
                        className={styles.chipDot}
                        style={{ background: chip.color }}
                      />
                    )}
                    <span className={styles.chipName}>{chip.name}</span>
                    <Icons.close />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button size="sm" variant="ghost" onClick={onReset}>
            {t('catalog.resetAll')}
          </Button>
        </div>
      )}
    </div>
  );
}
